import React, { useEffect, useState } from 'react';
import { api } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import useEffectiveRole from '../hooks/useEffectiveRole';
import TelemetryEditor from './TelemetryEditor';

/**
 * Native threaded discussion board scoped to a (module, topic) pair.
 * - Anyone signed in can post a thread or a reply.
 * - Author can delete own thread/reply.
 * - Developers + teachers can delete any thread/reply (moderation).
 */
const DiscussionBoard = ({ moduleId, topicId }) => {
  const { user } = useAuth();
  // Honor /preview-as-student — developer/faculty viewing as student
  // must not see moderation controls.
  const { effectiveRole } = useEffectiveRole();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newBodyTele, setNewBodyTele] = useState(null);
  const [posting, setPosting] = useState(false);

  const [replyOpen, setReplyOpen] = useState(null);  // thread id
  const [replyBody, setReplyBody] = useState('');
  const [replyBodyTele, setReplyBodyTele] = useState(null);
  const [replyPosting, setReplyPosting] = useState(false);

  const canModerate = ['developer', 'teacher'].includes(effectiveRole);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/api/discussions', {
        params: { module_id: moduleId, topic_id: topicId || undefined },
      });
      setThreads(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load discussion');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (moduleId) load(); /* eslint-disable-next-line */ }, [moduleId, topicId]);

  const createThread = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setPosting(true);
    try {
      await api.post('/api/discussions', {
        module_id: moduleId, topic_id: topicId || null,
        title: newTitle.trim(), body: newBody.trim(),
      });
      setNewTitle(''); setNewBody(''); setNewBodyTele(null);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to post');
    } finally { setPosting(false); }
  };

  const addReply = async (threadId) => {
    if (!replyBody.trim()) return;
    setReplyPosting(true);
    try {
      if (replyBodyTele) {
        // Use the v2 endpoint that persists telemetry on the reply.
        await api.post(`/api/discussions/${threadId}/replies-v2`, {
          text: replyBody.trim(),
          telemetry: replyBodyTele,
        });
      } else {
        await api.post(`/api/discussions/${threadId}/replies`, { body: replyBody.trim() });
      }
      setReplyBody(''); setReplyBodyTele(null); setReplyOpen(null);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reply');
    } finally { setReplyPosting(false); }
  };

  const deleteThread = async (t) => {
    if (!window.confirm('Delete this thread and all replies?')) return;
    try { await api.delete(`/api/discussions/${t.id}`); await load(); }
    catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  const deleteReply = async (threadId, replyId) => {
    if (!window.confirm('Delete this reply?')) return;
    try { await api.delete(`/api/discussions/${threadId}/replies/${replyId}`); await load(); }
    catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  const fmt = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const canDelete = (authorEmail) =>
    canModerate || (user && authorEmail === user.email);

  return (
    <div className="space-y-4" data-testid="discussion-board">
      {/* New thread composer */}
      <form onSubmit={createThread} className="card p-4 space-y-2" data-testid="new-thread-form">
        <input
          className="input-field"
          placeholder="Start a new discussion — title…"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          maxLength={200}
          data-testid="new-thread-title"
        />
        <TelemetryEditor
          value={newBody}
          onChange={setNewBody}
          placeholder="Optional context, question or prompt… (paste/keystroke telemetry captured · click ✨ to get a coaching authenticity score)"
          minHeight={80}
          context={newTitle}
          onTelemetryChange={setNewBodyTele}
          testidPrefix="new-thread-body"
        />        <div className="flex justify-end">
          <button type="submit" disabled={posting || !newTitle.trim()}
                  className="btn-primary text-sm" data-testid="post-thread-btn">
            {posting ? 'Posting…' : 'Post thread'}
          </button>
        </div>
      </form>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="text-gray-500 text-sm">Loading…</div>
      ) : threads.length === 0 ? (
        <div className="card p-8 text-center text-gray-500" data-testid="no-threads">
          No discussions yet. Start the first one above.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="threads-list">
          {threads.map((t) => (
            <li key={t.id} className="card p-4" data-testid={`thread-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h4 className="font-bold text-gray-900">{t.title}</h4>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {t.author_name} ·
                    <span className="badge-role ml-1">{t.author_role}</span> ·
                    <span className="ml-1">{fmt(t.created_at)}</span>
                  </div>
                </div>
                {canDelete(t.author_email) && (
                  <button onClick={() => deleteThread(t)}
                          className="text-xs text-red-600 hover:text-red-700 font-bold px-1.5"
                          data-testid={`thread-delete-${t.id}`} title="Delete thread">✕</button>
                )}
              </div>
              {t.body && (
                <div className="mt-2 text-sm text-gray-800 whitespace-pre-wrap">{t.body}</div>
              )}

              {/* Replies */}
              {(t.replies || []).length > 0 && (
                <ul className="mt-3 border-l-2 pl-3 space-y-2" style={{ borderColor: 'var(--unthsc-green-light)' }}>
                  {t.replies.map((r) => (
                    <li key={r.id} className="text-sm" data-testid={`reply-${r.id}`}>
                      <div className="text-[11px] text-gray-500">
                        {r.author_name} ·
                        <span className="badge-role ml-1">{r.author_role}</span> ·
                        <span className="ml-1">{fmt(r.created_at)}</span>
                        {canDelete(r.author_email) && (
                          <button onClick={() => deleteReply(t.id, r.id)}
                                  className="ml-2 text-red-600 hover:text-red-700 font-bold"
                                  data-testid={`reply-delete-${r.id}`} title="Delete reply">✕</button>
                        )}
                      </div>
                      <div className="text-gray-800 whitespace-pre-wrap mt-0.5">{r.body}</div>
                      {r.telemetry && (canModerate || (user && r.author_email === user.email)) && (
                        <div className="mt-1 text-[10px] text-gray-500 flex flex-wrap gap-2" data-testid={`reply-telemetry-${r.id}`}>
                          <span title="Keystrokes that produced visible chars">⌨ {r.telemetry.typed_chars || 0}</span>
                          <span title="Paste events"
                                className={r.telemetry.paste_events?.length ? 'text-amber-700 font-semibold' : ''}>
                            📋 {(r.telemetry.paste_events || []).length}
                            {(r.telemetry.paste_events || []).reduce((s, p) => s + (p.length || 0), 0) > 0
                              ? ` (${(r.telemetry.paste_events || []).reduce((s, p) => s + (p.length || 0), 0)} chars)` : ''}
                          </span>
                          <span title="Major edits">↩ {r.telemetry.revision_count || 0}</span>
                          <span title="Focused seconds">⏱ {r.telemetry.focused_seconds || 0}s</span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {/* Reply box */}
              {replyOpen === t.id ? (
                <div className="mt-3 space-y-2">
                  <TelemetryEditor
                    value={replyBody}
                    onChange={setReplyBody}
                    placeholder="Write a reply… your writing telemetry is captured. Click ✨ to get an authenticity check (signal, not verdict)."
                    minHeight={60}
                    context={t.title}
                    onTelemetryChange={setReplyBodyTele}
                    testidPrefix={`reply-${t.id}`}
                  />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setReplyOpen(null); setReplyBody(''); setReplyBodyTele(null); }}
                            className="btn-outline text-xs">Cancel</button>
                    <button onClick={() => addReply(t.id)} disabled={replyPosting || !replyBody.trim()}
                            className="btn-primary text-xs" data-testid={`post-reply-${t.id}`}>
                      {replyPosting ? 'Posting…' : 'Reply'}
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setReplyOpen(t.id); setReplyBody(''); setReplyBodyTele(null); }}
                        className="mt-3 text-xs font-semibold hover:underline"
                        style={{ color: 'var(--unthsc-blue)' }}
                        data-testid={`reply-open-${t.id}`}>
                  ↪ Reply
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DiscussionBoard;
